import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AIProvider } from '../services/aiService';
import { assistantChat } from '../services/aiAssistant';
import { AIContextData, formatAIContext } from '../utils/aiContext';
import { allowedReadTools } from '../constants/aiVisibility';
import { refreshIdentity, useUserStore } from './userStore';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { dedupeInit, isCloudFresh, markCloudFresh } from '../lib/cloudBoot';
import {
  accountGeneration, accountStorage, cachedUserIdOwner, scopeToAccount, swappingAccount,
} from '../lib/accountStorage';
import { createBlobSync, dropUnconfirmed, mergeTombstones, pruneTombstones, tombstone, type Tombstones } from '../lib/syncBlob';
import { supabaseBlobTransport } from '../lib/syncBlobTransport';
import { sameJson } from '../lib/syncDoc';
import { loadedFor, refreshBlobOnFocus } from '../lib/blobFocusRefresh';

// ─── Supabase sync (sync epic) — AI chat history as a blob per user ─────────
// `user_ai_chat.data` = `{ conversations, settings, usage, deleted }`.
//
// ⚠️⚠️ Writes go through syncBlob's read-modify-write, NOT a whole-row upsert.
// The old upsert wrote this tab's copy of every conversation, so a tab open
// since before a delete put the conversation back, and one open since before an
// Apply erased `suggestionAppliedAt` — re-arming a CHARGED Apply. Now every
// write re-reads the row and merges this tab INTO it: conversations by id
// (deletes are tombstones, as in the other syncBlob stores), messages by id
// inside a conversation, and an applied stamp from either side is kept.
const USER_AI_CHAT_TABLE = 'user_ai_chat';

const time = (v: unknown): number => {
  if (!v) return 0;
  const t = new Date(v as string | Date).getTime();
  return Number.isNaN(t) ? 0 : t;
};

function readTombstones(data: any): Tombstones {
  const raw = data?.deleted;
  return raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Tombstones) : {};
}

/**
 * Unique per message. ⚠️ The old `msg_${Date.now()}` gave a reply (or an instant
 * error) the SAME id as the question when both landed in one millisecond —
 * harmless while nothing merged by id, a lost message once something does.
 */
function newMessageId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

/** Old rows can hold a question and its reply under one id — the role tells them apart. */
const messageKey = (m: ChatMessage) => `${m.id}|${m.role}`;

/** Messages from both sides, one per id, in time order; anything at or before a clear is gone. */
function mergeMessages(local: ChatMessage[], cloud: ChatMessage[], clearedAt: number): ChatMessage[] {
  const byId = new Map<string, ChatMessage>();
  for (const m of cloud) byId.set(messageKey(m), m);
  for (const m of local) {
    const held = byId.get(messageKey(m));
    if (!held) { byId.set(messageKey(m), m); continue; }
    // ⚠️ The applied stamp is sticky: the EARLIEST one from either side survives.
    const applied = [held.suggestionAppliedAt, m.suggestionAppliedAt].filter(Boolean).sort()[0];
    byId.set(messageKey(m), { ...held, ...(applied ? { suggestionAppliedAt: applied } : {}) });
  }
  return [...byId.values()]
    .filter(m => clearedAt === 0 || time(m.timestamp) > clearedAt)
    .sort((a, b) => time(a.timestamp) - time(b.timestamp) || a.id.localeCompare(b.id));
}

/**
 * Union of both sides' conversations. A tombstone removes a conversation only
 * while it is newer than the conversation's own `updatedAt` — so a delete here
 * and a later message there resolves to whichever happened last, as in
 * `mergeCollection`.
 */
export function mergeConversations(
  local: Conversation[] | undefined,
  cloud: Conversation[] | undefined,
  deleted: Tombstones,
): Conversation[] {
  const byId = new Map<string, Conversation>();
  for (const c of cloud ?? []) byId.set(c.id, c);
  for (const l of local ?? []) {
    const c = byId.get(l.id);
    if (!c) { byId.set(l.id, l); continue; }
    const newer = time(l.updatedAt) > time(c.updatedAt) ? l : c;
    const clearedAt = Math.max(time(l.clearedAt), time(c.clearedAt));
    byId.set(l.id, {
      ...newer,
      ...(clearedAt ? { clearedAt: new Date(clearedAt).toISOString() } : {}),
      messages: mergeMessages(l.messages ?? [], c.messages ?? [], clearedAt),
    });
  }
  return [...byId.values()]
    .filter(c => !(deleted[c.id] && time(deleted[c.id]) >= time(c.updatedAt)))
    .sort((a, b) => time(b.createdAt) - time(a.createdAt) || a.id.localeCompare(b.id));
}

/** Counters only grow — keep the larger of each, whichever tab counted it. */
function mergeUsage(local: AIUsage | undefined, cloud: AIUsage | undefined): AIUsage | undefined {
  // A tab that has counted nothing has nothing to add (no write just to store zeros).
  const counted = local && (local.requestCount || local.tokensUsed || local.lastRequestAt) ? local : undefined;
  if (!counted || !cloud) return cloud ?? counted;
  return {
    tokensUsed: Math.max(counted.tokensUsed ?? 0, cloud.tokensUsed ?? 0),
    requestCount: Math.max(counted.requestCount ?? 0, cloud.requestCount ?? 0),
    lastRequestAt: time(counted.lastRequestAt) >= time(cloud.lastRequestAt) ? counted.lastRequestAt : cloud.lastRequestAt,
  };
}

interface ChatBlob {
  conversations: Conversation[];
  settings: AISettings | null;
  usage: AIUsage | undefined;
  deleted: Tombstones;
}

/** The parts of a tab that merge into the blob. */
interface ChatLocal {
  conversations: Conversation[];
  settings: AISettings | null;
  usage?: AIUsage;
  deleted: Tombstones;
  legacyIds: string[] | null;
}

const NOTHING_LOCAL: ChatLocal = { conversations: [], settings: null, deleted: {}, legacyIds: null };

/** This tab merged INTO a cloud blob (null = no row yet). */
function mergeChatBlob(local: ChatLocal, cloud: any): ChatBlob {
  const deleted = pruneTombstones(mergeTombstones(local.deleted, readTombstones(cloud)));
  const cloudConversations: Conversation[] = Array.isArray(cloud?.conversations) ? cloud.conversations : [];
  return {
    conversations: mergeConversations(
      dropUnconfirmed(local.conversations, cloudConversations, local.legacyIds),
      cloudConversations,
      deleted,
    ),
    // Legacy and inert (the assistant runs on the app proxy): the cloud's copy wins, as the load always had it.
    settings: cloud?.settings ?? local.settings ?? null,
    usage: mergeUsage(local.usage, cloud?.usage),
    deleted,
  };
}

/**
 * True while a merged cloud copy is being put into the store. The auto-sync
 * subscription skips it: what is adopted came FROM the cloud, so writing it
 * back would only re-download the whole blob to find nothing to write. (Edits
 * this tab still holds were scheduled when they were made; a refresh that
 * finds some schedules its own write.)
 */
let adoptingChat = false;

/** Bring a merged blob into this tab — only what really changed, so a no-op re-renders nothing. */
function adoptChat(merged: ChatBlob): void {
  const state = useAIChatStore.getState();
  const next: Partial<AIState> = {};
  if (!sameJson(state.conversations, merged.conversations)) next.conversations = merged.conversations;
  if (!sameJson(state.deleted, merged.deleted)) next.deleted = merged.deleted;
  if (!sameJson(state.usage, merged.usage) && merged.usage) next.usage = merged.usage;
  // The open conversation was deleted elsewhere: the next message starts a new one
  // rather than being added to nothing.
  if (state.currentConversationId && !merged.conversations.some(c => c.id === state.currentConversationId)) {
    next.currentConversationId = null;
  }
  if (!Object.keys(next).length) return;
  adoptingChat = true;
  try {
    useAIChatStore.setState(next);
  } finally {
    adoptingChat = false;
  }
}

const chatSync = createBlobSync({
  transport: supabaseBlobTransport(USER_AI_CHAT_TABLE),
  owner: () => useAIChatStore.getState().cachedUserId,
  onError: (err) => console.error('Failed to sync AI chat to Supabase:', err),
  build: (cloud) => {
    const merged = mergeChatBlob(useAIChatStore.getState(), cloud);
    if (cloud && sameJson(merged, mergeChatBlob(NOTHING_LOCAL, cloud))) {
      // Nothing of ours is missing from the cloud — but this tab may be missing
      // the cloud's (a delete or an Apply elsewhere): catch up.
      adoptChat(merged);
      return null;
    }
    return { ...(cloud && typeof cloud === 'object' ? cloud : {}), ...merged };
  },
  onWritten: (payload) => adoptChat(mergeChatBlob(useAIChatStore.getState(), payload)),
});

// A tab that comes back shows chats deleted and Applies made elsewhere
// (lib/blobFocusRefresh.ts) — the same merge as a write, folded into the tab.
refreshBlobOnFocus(USER_AI_CHAT_TABLE, () => chatSync.refresh(
  (cloud) => {
    const merged = mergeChatBlob(useAIChatStore.getState(), cloud);
    adoptChat(merged);
    return !sameJson(merged, mergeChatBlob(NOTHING_LOCAL, cloud)); // this tab holds what the cloud lacks
  },
  loadedFor(() => useAIChatStore.getState().initializedUserId),
));

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  suggestions?: any;
  /**
   * When this message's suggestion was applied.
   *
   * ⚠️⚠️ APPLYING USED TO LEAVE NO TRACE. `onApply` only closed the modal, so
   * the Apply button came straight back — and pressing it again **charged
   * again** and created a second copy of everything. The record has to live on
   * the MESSAGE, not in the host's local state, or it dies on reload and the
   * button is live again next session.
   */
  suggestionAppliedAt?: string;
  /** Write-tool proposals awaiting confirmation (Phase 2) — see ProposalPreview. */
  proposals?: import('../services/aiProxy').AIProposal[];
}

export interface Conversation {
  id: string;
  title: string;
  messages: ChatMessage[];
  createdAt: Date;
  updatedAt: Date;
  /**
   * When the conversation was last cleared (ISO). Messages are merged by id
   * across tabs, so a clear has to be a marker: without it another tab's copy
   * of the old messages would put them straight back.
   */
  clearedAt?: string;
}

interface AISettings {
  provider: AIProvider;
  apiKey: string;
  model?: string;
  useMockMode?: boolean;
}

interface AIUsage {
  tokensUsed: number;
  requestCount: number;
  lastRequestAt: Date | null;
}

/** Daily app-owned-proxy usage, surfaced as the "X left today" indicator. */
interface AIDailyUsage {
  used: number;
  limit: number;
}

interface AIState {
  conversations: Conversation[];
  currentConversationId: string | null;
  isOpen: boolean;
  isLoading: boolean;
  settings: AISettings | null;
  usage: AIUsage;
  dailyUsage: AIDailyUsage | null;
  /** True when the most recent reply came from the offline mock (proxy capped/down). */
  lastReplyMock: boolean;
  contextData: AIContextData | null;
  /** id → deletedAt. Without this, the union-on-write would resurrect deletes. */
  deleted: Tombstones;
  /**
   * Conversation ids held by a cache from before union writes, not yet confirmed
   * by a cloud load — see `dropUnconfirmed`. `null` once a load has run.
   */
  legacyIds: string[] | null;
  initializedUserId: string | null;
  cachedUserId: string | null;
  initialize: () => Promise<void>;

  setOpen: (open: boolean) => void;
  setSettings: (settings: AISettings) => void;
  setContextData: (data: AIContextData) => void;

  createConversation: (title?: string) => string;
  deleteConversation: (id: string) => void;
  setCurrentConversation: (id: string) => void;

  sendMessage: (content: string) => Promise<void>;
  clearConversation: (id: string) => void;
  updateConversationTitle: (id: string, title: string) => void;
  markSuggestionApplied: (messageId: string) => void;

  parseSuggestion: (content: string) => any | null;
  updateUsage: (tokensUsed: number) => void;
}

/** What `ai-chat-storage` holds (see `partialize`). */
type PersistedChat = Pick<
  AIState,
  'conversations' | 'deleted' | 'legacyIds' | 'currentConversationId' | 'settings' | 'usage' | 'cachedUserId'
>;

export const useAIChatStore = create<AIState>()(
  persist<AIState, [], [], PersistedChat>(
    (set, get) => ({
      conversations: [],
      deleted: {},
      legacyIds: null,
      currentConversationId: null,
      isOpen: false,
      isLoading: false,
      initializedUserId: null,
      cachedUserId: null,

      initialize: () => dedupeInit('user_ai_chat', async () => {
        const at = accountGeneration();
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;
          if (get().initializedUserId === user.id) return;
          // Cache-first boot: while this user's local cache is fresh, skip the
          // cloud read entirely (write-through keeps cloud current meanwhile).
          // ⚠️ A pre-union cache must be confirmed by a real load first (legacyIds).
          if (get().cachedUserId === user.id && !get().legacyIds && isCloudFresh('user_ai_chat', user.id)) {
            set({ initializedUserId: user.id });
            return;
          }
          const { data, error } = await supabase.from(USER_AI_CHAT_TABLE).select('*');
          // An answer for an account that has since signed out is not this copy's.
          if (at !== accountGeneration()) return;
          if (error) throw error;
          const row = (data ?? [])[0];
          const folded = !!(row && row.data && Array.isArray(row.data.conversations));
          // No suppression flag: a merge that changed nothing hands back the
          // SAME references (the subscription then writes nothing). The write-back
          // is decided below, against the cloud copy.
          set((state) => {
            const cacheIsOurs = state.cachedUserId === null || state.cachedUserId === user.id;
            if (folded) {
              const merged = mergeChatBlob(cacheIsOurs ? state : NOTHING_LOCAL, row.data);
              return {
                conversations: sameJson(merged.conversations, state.conversations) ? state.conversations : merged.conversations,
                deleted: sameJson(merged.deleted, state.deleted) ? state.deleted : merged.deleted,
                settings: row.data.settings ?? state.settings,
                usage: merged.usage && !sameJson(merged.usage, state.usage) ? merged.usage : state.usage,
                legacyIds: null,
                initializedUserId: user.id,
                cachedUserId: user.id,
              };
            }
            return cacheIsOurs
              ? { legacyIds: null, initializedUserId: user.id, cachedUserId: user.id }
              : { conversations: [], deleted: {}, legacyIds: null, initializedUserId: user.id, cachedUserId: user.id };
          });
          chatSync.noteLoaded(folded ? row.updated_at ?? null : null);
          // ⚠️ This tab holds what the cloud copy lacks (a chat whose write was skipped while
          // signed out, a first chat with no row yet): written now. Such a merge equals this
          // tab's copy — no reference changes, so the subscription never sees it.
          const cloud = row?.data ?? null;
          if (!sameJson(mergeChatBlob(get(), cloud), mergeChatBlob(NOTHING_LOCAL, cloud))) chatSync.schedule();
          markCloudFresh('user_ai_chat', user.id);
        } catch (error) {
          console.error('Failed to initialize AI chat from Supabase:', error);
        }
      }),
      settings: null,
      usage: {
        tokensUsed: 0,
        requestCount: 0,
        lastRequestAt: null
      },
      dailyUsage: null,
      lastReplyMock: false,
      contextData: null,

      setOpen: (open) => set({ isOpen: open }),

      // Retained for backward-compat with already-synced data; the assistant runs
      // on the app-owned proxy, so these settings no longer drive any AI call.
      setSettings: (settings) => set({ settings }),

      setContextData: (data) => set({ contextData: data }),

      createConversation: (title = 'New Conversation') => {
        // Reuse an existing empty conversation instead of stacking up blank ones.
        const existingEmpty = get().conversations.find((c) => c.messages.length === 0);
        if (existingEmpty) {
          set({ currentConversationId: existingEmpty.id });
          return existingEmpty.id;
        }

        const id = `conv_${Date.now()}`;
        const newConversation: Conversation = {
          id,
          title,
          messages: [],
          createdAt: new Date(),
          updatedAt: new Date()
        };

        set((state) => ({
          conversations: [newConversation, ...state.conversations],
          currentConversationId: id
        }));

        return id;
      },

      // ⚠️ Deleting must leave a tombstone. Writes are a union now, so a bare
      // filter would be undone by the very next merge against the cloud copy.
      deleteConversation: (id) => {
        set((state) => ({
          conversations: state.conversations.filter(c => c.id !== id),
          deleted: tombstone(state.deleted, id),
          currentConversationId: state.currentConversationId === id ? null : state.currentConversationId
        }));
      },

      setCurrentConversation: (id) => {
        set({ currentConversationId: id });
      },

      sendMessage: async (content) => {
        const { currentConversationId, conversations, contextData } = get();

        // No API key required — the assistant runs on the app-owned ai-proxy.

        if (!currentConversationId) {
          get().createConversation();
        }

        const convId = get().currentConversationId;
        if (!convId) return;

        const userMessage: ChatMessage = {
          id: newMessageId(),
          role: 'user',
          content,
          timestamp: new Date()
        };

        set((state) => ({
          conversations: state.conversations.map(c =>
            c.id === convId
              ? {
                  ...c,
                  // Title the conversation from its first user message.
                  title: c.messages.length === 0 ? content.slice(0, 40) : c.title,
                  messages: [...c.messages, userMessage],
                  updatedAt: new Date()
                }
              : c
          ),
          isLoading: true
        }));

        try {
          const conversation = conversations.find(c => c.id === convId);
          const priorMessages = conversation?.messages || [];

          const proxyMessages: { role: 'user' | 'assistant'; content: string }[] = [
            ...priorMessages.map(m => ({ role: m.role, content: m.content })),
            { role: 'user' as const, content },
          ];

          // ⚠️⚠️ BOTH HALVES OF THE VISIBILITY SWITCHES, and they are enforced
          // in different places: the context is filtered HERE (the refused
          // bytes never leave the device), while the read tools can only be
          // filtered by `ai-proxy` — so the allowed set has to travel with the
          // request. See `constants/aiVisibility.ts`.
          // ⚠️⚠️ Read from the CLOUD's copy, not just this tab's: a tab open since
          // before a refusal on another device would otherwise still send that
          // source. The refresh costs a timestamp unless the identity row moved.
          await refreshIdentity();
          const visibility = useUserStore.getState().aiVisibility;
          const context = contextData ? formatAIContext(contextData, visibility) : undefined;

          const reply = await assistantChat({
            messages: proxyMessages,
            context,
            allowedTools: allowedReadTools(visibility),
          });

          const suggestion = get().parseSuggestion(reply.content);

          const assistantMessage: ChatMessage = {
            id: newMessageId(),
            role: 'assistant',
            content: reply.content,
            timestamp: new Date(),
            suggestions: suggestion,
            ...(reply.proposals?.length ? { proposals: reply.proposals } : {}),
          };

          set((state) => ({
            conversations: state.conversations.map(c =>
              c.id === convId
                ? {
                    ...c,
                    messages: [...c.messages, assistantMessage],
                    updatedAt: new Date()
                  }
                : c
            ),
            isLoading: false,
            dailyUsage: reply.usage ?? state.dailyUsage,
            lastReplyMock: reply.mock,
          }));

          get().updateUsage(0);

        } catch (error) {
          console.error('Failed to send message:', error);

          const errorMessage: ChatMessage = {
            id: newMessageId(),
            role: 'assistant',
            content: `Sorry, I ran into a problem reaching the assistant: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again in a moment.`,
            timestamp: new Date()
          };

          set((state) => ({
            conversations: state.conversations.map(c =>
              c.id === convId
                ? {
                    ...c,
                    messages: [...c.messages, errorMessage],
                    updatedAt: new Date()
                  }
                : c
            ),
            isLoading: false
          }));
        }
      },

      clearConversation: (id) => {
        set((state) => ({
          conversations: state.conversations.map(c =>
            c.id === id
              ? { ...c, messages: [], clearedAt: new Date().toISOString(), updatedAt: new Date() }
              : c
          )
        }));
      },

      /**
       * Stamp a message's suggestion as applied so its button retires.
       *
       * ⚠️ Searches EVERY conversation rather than the current one: the panel
       * and the full page share this store, and a user can switch conversations
       * while an apply is in flight.
       */
      markSuggestionApplied: (messageId: string) => {
        set((state) => ({
          conversations: state.conversations.map((c) =>
            c.messages.some((m) => m.id === messageId)
              ? {
                ...c,
                messages: c.messages.map((m) =>
                  m.id === messageId && !m.suggestionAppliedAt
                    ? { ...m, suggestionAppliedAt: new Date().toISOString() }
                    : m,
                ),
              }
              : c,
          ),
        }));
      },

      updateConversationTitle: (id, title) => {
        set((state) => ({
          conversations: state.conversations.map(c =>
            c.id === id
              ? { ...c, title, updatedAt: new Date() }
              : c
          )
        }));
      },

      parseSuggestion: (content) => {
        const jsonMatch = content.match(/```json\s*([\s\S]*?)\s*```/);
        if (jsonMatch) {
          try {
            return JSON.parse(jsonMatch[1]);
          } catch {
            return null;
          }
        }

        const codeBlockMatch = content.match(/```\s*([\s\S]*?)\s*```/);
        if (codeBlockMatch) {
          try {
            return JSON.parse(codeBlockMatch[1]);
          } catch {
            return null;
          }
        }

        try {
          const lines = content.split('\n');
          for (const line of lines) {
            if (line.trim().startsWith('{')) {
              const jsonEnd = content.indexOf('}', content.indexOf(line)) + 1;
              if (jsonEnd > 0) {
                const jsonStr = content.slice(content.indexOf(line), jsonEnd);
                return JSON.parse(jsonStr);
              }
            }
          }
        } catch {
          return null;
        }

        return null;
      },

      updateUsage: (tokensUsed) => {
        set((state) => ({
          usage: {
            tokensUsed: state.usage.tokensUsed + tokensUsed,
            requestCount: state.usage.requestCount + 1,
            lastRequestAt: new Date()
          }
        }));
      }
    }),
    {
      name: 'ai-chat-storage',
      // ⭐ One saved copy per account — see `scopeToAccount` below.
      storage: accountStorage(cachedUserIdOwner),
      // `deleted` MUST be persisted: a tombstone that dies on reload lets the
      // next merge resurrect the conversation it was suppressing.
      // v1 = union writes. A v0 cache's conversations must be confirmed by one
      // cloud load before they can be written back (see `dropUnconfirmed`).
      version: 1,
      migrate: (persisted, version) => {
        const p = persisted as PersistedChat;
        if (version < 1 && p && Array.isArray(p.conversations)) {
          return { ...p, legacyIds: p.conversations.map((c) => c.id) } as AIState;
        }
        return p as AIState;
      },
      partialize: (state) => ({
        conversations: state.conversations,
        deleted: state.deleted,
        legacyIds: state.legacyIds,
        currentConversationId: state.currentConversationId,
        settings: state.settings,
        usage: state.usage,
        cachedUserId: state.cachedUserId,
      })
    }
  )
);

// Auto-sync on change (debounced, merged into the cloud copy — see chatSync).
useAIChatStore.subscribe((state, prev) => {
  if (swappingAccount()) return; // a change of account swaps the saved copy in — not an edit
  if (adoptingChat) return; // the cloud's own copy — see adoptingChat
  if (
    state.conversations === prev.conversations && state.settings === prev.settings
    && state.usage === prev.usage && state.deleted === prev.deleted
  ) return;
  chatSync.schedule();
});

/* ⚠️⚠️ ONE SAVED COPY PER ACCOUNT (lib/accountStorage). The device kept one copy, tagged with the
   account it was loaded for, and a sign-out does not reload the page: an account's edits whose
   write had not landed lived only until the next account's load replaced the copy, and the next
   account's edits made before its own load were merged into the last one's row by ITS next load. */
scopeToAccount(useAIChatStore);

/** Await the pending AI chat write (tests). */
export function flushAIChatSync(): Promise<void> {
  return chatSync.flush();
}
